"use client";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { mediaCoverage as data } from "@/lib/data/press";

const accent = "var(--color-cyan)";

const renderTable = (rows: { publication?: string; article?: string; firm?: string; report?: string; date: string }[], dateField: "date") => (
  <div className="glass rounded-2xl overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[rgb(var(--border)/0.18)] bg-[rgb(var(--border)/0.04)]">
            <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Publication</th>
            <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Article</th>
            <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))] hidden md:table-cell">Date</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-[rgb(var(--border)/0.08)]">
              <td className="px-4 py-3 align-top font-medium">{r.publication ?? r.firm}</td>
              <td className="px-4 py-3 align-top text-[rgb(var(--fg-muted))]">{r.article ?? r.report}</td>
              <td className="px-4 py-3 align-top font-mono text-xs hidden md:table-cell" style={{ color: accent }}>{r[dateField]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export function MediaCoverage() {
  return (
    <Section id="media-coverage" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-cyan)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <div className="mt-10">
            <h3 className="text-lg mb-3">Technology Coverage</h3>
            {renderTable(data.technology, "date")}
          </div>
        </Reveal>
        <Reveal delay={150}>
          <div className="mt-8">
            <h3 className="text-lg mb-3">Business & Strategy Coverage</h3>
            {renderTable(data.business, "date")}
          </div>
        </Reveal>
        <Reveal delay={200}>
          <div className="mt-8">
            <h3 className="text-lg mb-3">Government & Public Sector Coverage</h3>
            {renderTable(data.government, "date")}
          </div>
        </Reveal>
        <Reveal delay={250}>
          <div className="mt-8">
            <h3 className="text-lg mb-3">Industry Analyst Coverage</h3>
            {renderTable(data.analyst, "date")}
          </div>
        </Reveal>
        <Reveal delay={500}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link key={c.label} href={c.href} className={i === 0 ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm" : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"}>
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
